import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Paper
} from "@mui/material";
import {
  AccountBalanceWallet,
  ArrowDownward,
  ArrowUpward,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Visibility,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Edit as EditIcon
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../../Configurations/Appservice";
import moment from "moment";

const CustomerWallets = () => {
  const dispatch = useDispatch();

  // State
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Wallet data
  const [walletBalance, setWalletBalance] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [topupRequests, setTopupRequests] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);

  // Dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', request: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, request: null });

  useEffect(() => {
    dispatch(updateScreenTitle("Customer Wallets"));
    loadOrganizations();
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  const loadOrganizations = async () => {
    try {
      const response = await AppService.get("v1/organisation/keyValues?type=CUSTOMER");
      const orgs = Array.isArray(response) ? response : response?.data || [];
      setOrganizations(orgs);
    } catch (error) {
      console.error("Failed to load organizations:", error);
    }
  };

  const loadWalletData = async (orgId) => {
    if (!orgId) {
      console.warn('loadWalletData called without orgId');
      return;
    }

    console.log('loadWalletData: Fetching data for orgId:', orgId);
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [balanceRes, summaryRes, topupsRes, transactionsRes] = await Promise.all([
        AppService.get(`v1/wallet/transactions/balance/${orgId}`).catch((err) => {
          console.log('Balance API error:', err);
          return null;
        }),
        AppService.get(`v1/topupRequest/summary/${orgId}`).catch((err) => {
          console.log('Summary API error:', err);
          return null;
        }),
        AppService.get(`v1/topupRequest/organization/${orgId}`).catch((err) => {
          console.log('Topups API error:', err);
          return [];
        }),
        AppService.get(`v1/wallet/transactions/topups?organizationId=${orgId}`).catch((err) => {
          console.log('Transactions API error:', err);
          return [];
        })
      ]);

      console.log('loadWalletData: API responses for orgId:', orgId, {
        balance: balanceRes,
        summary: summaryRes,
        topups: topupsRes,
        transactions: transactionsRes
      });

      setWalletBalance(balanceRes);
      setPaymentSummary(summaryRes);
      setTopupRequests(Array.isArray(topupsRes) ? topupsRes : []);
      setWalletTransactions(Array.isArray(transactionsRes) ? transactionsRes : []);
    } catch (error) {
      console.error("Failed to load wallet data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgChange = (event, newValue) => {
    // Always clear existing data first when switching organizations
    setWalletBalance(null);
    setPaymentSummary(null);
    setTopupRequests([]);
    setWalletTransactions([]);

    setSelectedOrg(newValue);
    if (newValue) {
      const orgId = newValue.id || newValue.value;
      console.log('Loading wallet data for org:', orgId, newValue);
      loadWalletData(orgId);
    }
  };

  const handleApprove = async () => {
    if (!confirmDialog.request) return;
    dispatch(showLoader());
    try {
      await AppService.put(`v1/topupRequest/approve?requestId=${confirmDialog.request.id}`);
      // Refresh data
      const orgId = selectedOrg?.id || selectedOrg?.value;
      loadWalletData(orgId);
      setConfirmDialog({ open: false, type: '', request: null });
    } catch (error) {
      console.error("Failed to approve:", error);
      alert("Failed to approve topup request");
    } finally {
      dispatch(dismissLoader());
    }
  };

  const handleReject = async () => {
    if (!confirmDialog.request) return;
    dispatch(showLoader());
    try {
      await AppService.put(`v1/topupRequest/reject?requestId=${confirmDialog.request.id}`);
      // Refresh data
      const orgId = selectedOrg?.id || selectedOrg?.value;
      loadWalletData(orgId);
      setConfirmDialog({ open: false, type: '', request: null });
    } catch (error) {
      console.error("Failed to reject:", error);
      alert("Failed to reject topup request");
    } finally {
      dispatch(dismissLoader());
    }
  };

  const handleRefresh = () => {
    if (selectedOrg) {
      const orgId = selectedOrg.id || selectedOrg.value;
      loadWalletData(orgId);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      PENDING: { color: 'warning', icon: <Pending fontSize="small" /> },
      APPROVED: { color: 'success', icon: <CheckCircle fontSize="small" /> },
      REJECTED: { color: 'error', icon: <Cancel fontSize="small" /> },
      COMPLETED: { color: 'success', icon: <CheckCircle fontSize="small" /> }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <Chip
        size="small"
        label={status}
        color={config.color}
        icon={config.icon}
        sx={{ fontWeight: 500 }}
      />
    );
  };

  const formatAmount = (amount) => {
    return `AED ${Number(amount || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Card styling matching Finance Overview - consistent teal theme
  const cardStyle = {
    p: 2,
    height: '100%',
    minHeight: 120,
    background: 'linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)',
    color: 'white',
    borderRadius: 2,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 16px rgba(26, 95, 107, 0.3)',
    },
  };

  // Card variations matching Finance Overview
  const cardStyles = {
    primary: {
      ...cardStyle,
      background: 'linear-gradient(135deg, #1a7f8f 0%, #156b7b 100%)',
    },
    secondary: {
      ...cardStyle,
      background: 'linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)',
    },
    tertiary: {
      ...cardStyle,
      background: 'linear-gradient(135deg, #155566 0%, #0f4656 100%)',
    },
  };

  return (
    <Box sx={{ px: 3, py: 3 }}>
      {/* Organization Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Autocomplete
              options={organizations}
              getOptionLabel={(option) => {
                const name = option.name || option.label || '';
                const code = option.code || option.value || '';
                return code ? `${name} (${code})` : name;
              }}
              value={selectedOrg}
              onChange={handleOrgChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer Organization"
                  placeholder="Search organizations..."
                  variant="outlined"
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => (option.id || option.value) === (value.id || value.value)}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {selectedOrg && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{ borderColor: '#1a7f8f', color: '#1a7f8f', '&:hover': { borderColor: '#156b7b', bgcolor: 'rgba(26, 127, 143, 0.08)' } }}
              >
                REFRESH
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body1" ml={2} color="text.secondary">
            Loading wallet data...
          </Typography>
        </Box>
      )}

      {/* Content - Only show when org selected and not loading */}
      {selectedOrg && !loading && (
        <>
          {/* Organization Info */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Organization Name</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedOrg.name || selectedOrg.label || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Code</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedOrg.code || selectedOrg.organisationCode || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box>
                  <Chip label="Active" color="success" size="small" />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Wallet Summary Cards - 3 cards like Finance Overview */}
          <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
            {/* Card 1: Wallet Balance */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={cardStyles.primary}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', height: '100%' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 1 }}>
                      Wallet Balance
                    </Typography>
                    <Box>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>
                        {formatAmount(walletBalance?.balance || paymentSummary?.currentWalletBalance || 0)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75, mt: 0.5 }}>
                        Current available balance
                      </Typography>
                    </Box>
                  </Box>
                  <AccountBalanceWallet sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
                </Box>
              </Paper>
            </Grid>

            {/* Card 2: Topup Requests (Approved + Pending + Rejected) */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={cardStyles.secondary}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.6 }}>
                      Topup Requests
                    </Typography>

                    {/* Approved Section */}
                    <Box sx={{ mb: 0.5, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#4caf50' }}>
                            {formatAmount(paymentSummary?.totalApprovedAmount || 0)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                            (Approved)
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                          {paymentSummary?.approvedCount || 0}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Pending Section */}
                    <Box sx={{ mb: 0.5, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#ff9800' }}>
                            {formatAmount(paymentSummary?.totalPendingAmount || 0)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                            (Pending)
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                          {paymentSummary?.pendingCount || 0}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Rejected Section */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#f44336' }}>
                            {formatAmount(paymentSummary?.totalRejectedAmount || 0)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                            (Rejected)
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                          {paymentSummary?.rejectedCount || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <ArrowDownward sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
                </Box>
              </Paper>
            </Grid>

            {/* Card 3: Ticket Payments (Paid + Unpaid) */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={cardStyles.tertiary}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#ffffff', opacity: 0.85, mb: 0.6 }}>
                      Ticket Payments
                    </Typography>

                    {/* Paid Section */}
                    <Box sx={{ mb: 0.5, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#4caf50' }}>
                            {formatAmount(paymentSummary?.totalPaidTicketsAmount || 0)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                            (Paid)
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                          {paymentSummary?.paidTicketsCount || 0}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Unpaid Section */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#ff9800' }}>
                            {formatAmount(paymentSummary?.totalUnpaidTicketsAmount || 0)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#ffffff', opacity: 0.75 }}>
                            (Unpaid)
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffff', opacity: 0.75 }}>
                          {paymentSummary?.unpaidTicketsCount || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <ArrowUpward sx={{ fontSize: 28, color: '#ffffff', opacity: 0.6 }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper sx={{ p: 0 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
                <Tab label={`TOPUP REQUESTS (${topupRequests.length})`} sx={{ fontWeight: 600 }} />
                <Tab label={`WALLET TRANSACTIONS (${walletTransactions.length})`} sx={{ fontWeight: 600 }} />
              </Tabs>
            </Box>

            {/* Tab 0: Topup Requests */}
            {currentTab === 0 && (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Bank</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topupRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No topup requests found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      topupRequests.map((req) => (
                        <TableRow key={req.id} hover sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                          <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.875rem' }}>
                              {formatAmount(req.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>{getStatusChip(req.status)}</TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>{req.destinationBank || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Chip
                              size="small"
                              label={req.tags?.source || 'DIRECT'}
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {moment(req.transactionDate || req.createDateTime).format('MMM DD, YYYY')}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => setDetailDialog({ open: true, request: req })}
                                sx={{ color: '#006B63', '&:hover': { backgroundColor: 'rgba(0, 107, 99, 0.08)' } }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 1: Wallet Transactions */}
            {currentTab === 1 && (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Transaction #</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: '#006B63', color: 'white', fontSize: '0.875rem' }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {walletTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No transactions found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      walletTransactions.map((txn) => (
                        <TableRow key={txn.id} hover sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {txn.transactionNumber || txn.id?.substring(0, 8)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Chip
                              size="small"
                              label={txn.type}
                              color={txn.type === 'CREDIT' ? 'success' : 'error'}
                              icon={txn.type === 'CREDIT' ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Typography
                              fontWeight={600}
                              color={txn.type === 'CREDIT' ? 'success.main' : 'error.main'}
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {txn.type === 'CREDIT' ? '+' : '-'} {formatAmount(txn.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>{getStatusChip(txn.status)}</TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Typography sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>
                              {txn.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {moment(txn.createdDate).format('MMM DD, YYYY HH:mm')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Empty state when no org selected */}
      {!selectedOrg && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <AccountBalanceWallet sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a customer organization to view wallet details
          </Typography>
        </Box>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: '', request: null })}>
        <DialogTitle>
          {confirmDialog.type === 'approve' ? 'Approve Topup Request' : 'Reject Topup Request'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.type} this topup request for{' '}
            <strong>{formatAmount(confirmDialog.request?.amount)}</strong>?
          </Typography>
          {confirmDialog.type === 'approve' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This will credit the customer's wallet with the approved amount.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', request: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.type === 'approve' ? 'success' : 'error'}
            onClick={confirmDialog.type === 'approve' ? handleApprove : handleReject}
          >
            {confirmDialog.type === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog - Matching Payment Approvals Design */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, request: null })} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', bgcolor: '#006B63', color: 'white', mb: 0 }}>
          Payment Details - {detailDialog.request?.transactionNumber || detailDialog.request?.id?.substring(0, 8) || 'N/A'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {detailDialog.request && (
            <Grid container spacing={2}>
              {/* Left Column */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Transaction Number</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.95rem', mt: 0.5, fontWeight: 600, color: '#006B63' }}>
                    {detailDialog.request.transactionNumber || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Customer</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', mt: 0.5 }}>
                    {selectedOrg?.name || selectedOrg?.label || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3', fontSize: '1.2rem', mt: 0.5 }}>
                    {formatAmount(detailDialog.request.amount)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Destination Bank</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', mt: 0.5 }}>
                    {detailDialog.request.destinationBank || 'Not Assigned'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Thaklis Bank Account</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', mt: 0.5, fontFamily: 'monospace', fontWeight: 500 }}>
                    Thaklis Bank - Main Account
                  </Typography>
                </Box>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Payment Source</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {detailDialog.request.tags?.source === 'TYPING_CENTER' ? (
                      <Chip
                        label="VIA TYPING CENTER"
                        size="small"
                        sx={{
                          bgcolor: '#E8F5E9',
                          color: '#2E7D32',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    ) : (
                      <Chip
                        label="DIRECT CUSTOMER"
                        size="small"
                        sx={{
                          bgcolor: '#E3F2FD',
                          color: '#1565C0',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Typing Center Details (if applicable) */}
                {detailDialog.request.tags?.source === 'TYPING_CENTER' && detailDialog.request.tags?.typingCenterName && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: '#E8F5E9', borderRadius: 1, border: '1px solid #C8E6C9' }}>
                    <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase' }}>Typing Center</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25, color: '#1B5E20' }}>
                      {detailDialog.request.tags.typingCenterName}
                      {detailDialog.request.tags.typingCenterCode && ` (${detailDialog.request.tags.typingCenterCode})`}
                    </Typography>
                    {detailDialog.request.tags.invoiceNumber && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#388E3C' }}>
                        Invoice: {detailDialog.request.tags.invoiceNumber}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Description</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', mt: 0.5 }}>
                    {detailDialog.request.paymentMethod || 'Bank Transfer'} Payment
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Transaction Date</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', mt: 0.5 }}>
                    {moment(detailDialog.request.transactionDate || detailDialog.request.createDateTime).format('DD MMM YYYY')}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(detailDialog.request.status)}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Proof Document</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: '#666' }}>
                    {detailDialog.request.proofDocumentPath ? 'Document uploaded' : 'No uploaded proof document found.'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDetailDialog({ open: false, request: null })} sx={{ color: '#666' }}>
            CLOSE
          </Button>
          {detailDialog.request?.status === 'PENDING' && (
            <>
              <Button
                variant="outlined"
                startIcon={<RejectIcon />}
                size="small"
                onClick={() => {
                  setConfirmDialog({ open: true, type: 'reject', request: detailDialog.request });
                  setDetailDialog({ open: false, request: null });
                }}
                sx={{ borderColor: '#f44336', color: '#f44336', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.04)', borderColor: '#f44336' } }}
              >
                REJECT
              </Button>
              <Button
                variant="contained"
                startIcon={<ApproveIcon />}
                onClick={() => {
                  setConfirmDialog({ open: true, type: 'approve', request: detailDialog.request });
                  setDetailDialog({ open: false, request: null });
                }}
                sx={{ bgcolor: '#006B63', '&:hover': { bgcolor: '#004d47' } }}
              >
                APPROVE
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerWallets;
